import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { theme } from '../../../../core/design-system/theme'
import { useBookingContext, BookingStepShell } from '../BookingFlowView'

const prefersReducedMotion =
  typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false

function StarIcon({ filled }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
        fill={filled ? theme.colors.honeyGold : 'none'}
        stroke={theme.colors.honeyGold}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Stars({ rating }) {
  const rounded = Math.round(rating)
  return (
    <span style={{ display: 'inline-flex', gap: 2, alignItems: 'center' }}>
      {Array.from({ length: 5 }, (_, i) => (
        <StarIcon key={i} filled={i < rounded} />
      ))}
    </span>
  )
}

export default function BookingStep4View() {
  const { viewModel, repository } = useBookingContext()
  const navigate = useNavigate()

  const [nonnas, setNonnas] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(null)
  const [selectedNonna, setSelectedNonna] = useState(viewModel.draft.selectedNonna ?? null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setFetchError(null)
    repository
      .fetchFeaturedNonnas()
      .then((data) => {
        if (!cancelled) {
          setNonnas(data)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setFetchError(err.message || 'Could not load Nonnas. Please try again.')
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [repository])

  function handleContinue() {
    if (!selectedNonna) return
    viewModel.setNonna(selectedNonna)
    navigate('/book/review')
  }

  return (
    <BookingStepShell
      step={4}
      title="Choose your Nonna"
      onBack={() => navigate(-1)}
      onContinue={handleContinue}
      continueDisabled={!selectedNonna}
    >
      {loading && (
        <p
          style={{
            color: theme.colors.textSecondary,
            fontFamily: 'var(--font-body)',
            textAlign: 'center',
            marginTop: 40,
          }}
        >
          Finding available Nonnas…
        </p>
      )}

      {fetchError && (
        <p
          role="alert"
          style={{
            color: theme.colors.errorText,
            fontFamily: 'var(--font-body)',
            textAlign: 'center',
            background: theme.colors.errorBg,
            borderRadius: theme.layout.inputRadius,
            padding: '12px 16px',
          }}
        >
          {fetchError}
        </p>
      )}

      {!loading && !fetchError && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {nonnas.map((nonna) => {
            const isSelected = selectedNonna?.id === nonna.id
            return (
              <button
                key={nonna.id}
                onClick={() => setSelectedNonna(nonna)}
                aria-pressed={isSelected}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: theme.colors.card,
                  border: `1.5px solid ${isSelected ? theme.colors.terracotta : theme.colors.border}`,
                  borderRadius: theme.layout.cardRadius,
                  padding: '14px 16px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  transition: prefersReducedMotion ? 'none' : 'border-color 0.15s ease',
                }}
              >
                {/* Avatar */}
                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: '50%',
                    background: theme.colors.peach,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: 20,
                    fontWeight: 700,
                    color: theme.colors.terracottaDark,
                    fontFamily: 'var(--font-display)',
                  }}
                  aria-hidden="true"
                >
                  {nonna.name.charAt(0).toUpperCase()}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: theme.colors.textPrimary,
                      fontFamily: 'var(--font-body)',
                    }}
                  >
                    {nonna.name}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      marginTop: 3,
                    }}
                  >
                    <Stars rating={nonna.rating} />
                    <span
                      style={{
                        fontSize: 12,
                        color: theme.colors.textSecondary,
                        fontFamily: 'var(--font-body)',
                      }}
                    >
                      {nonna.rating.toFixed(1)} ({nonna.reviewCount})
                    </span>
                  </div>
                </div>

                {/* Rate */}
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: isSelected ? theme.colors.terracotta : theme.colors.textPrimary,
                    fontFamily: 'var(--font-body)',
                    flexShrink: 0,
                  }}
                >
                  ${nonna.hourlyRate}/hr
                </div>
              </button>
            )
          })}
        </div>
      )}
    </BookingStepShell>
  )
}
